import styled from '@emotion/styled'
import { useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { Responsive } from 'react-grid-layout'

import { WidgetId, WidgetLayout } from './types/dashboard.type'
import BadnetStatsWidget from './widgets/BadnetStatsWidget'
import NewsStatsWidget from './widgets/NewsStatsWidget'
import NotificationsWidget from './widgets/NotificationsWidget'
import QuickActionsWidget from './widgets/QuickActionsWidget'
import ShopStatsWidget from './widgets/ShopStatsWidget'
import UserStatsWidget from './widgets/UserStatsWidget'

// Import CSS for react-grid-layout
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

interface DashboardGridProps {
    enabledWidgets: WidgetId[]
    layouts?: Record<string, WidgetLayout[]>
    isEditable?: boolean
    onLayoutChange?: (layouts: Record<string, WidgetLayout[]>) => void
}

const widgetComponents: Record<WidgetId, React.ComponentType> = {
    'user-stats': UserStatsWidget,
    'shop-stats': ShopStatsWidget,
    'news-stats': NewsStatsWidget,
    'badnet-stats': BadnetStatsWidget,
    'quick-actions': QuickActionsWidget,
    notifications: NotificationsWidget,
}

const GridContainer = styled.div`
    padding: 0;
`

const WidgetWrapper = styled.div<{ isEditable: boolean; isMobile: boolean }>`
    height: 100%;

    .react-grid-layout {
        transition: height 0.2s ease;
    }

    .react-grid-item {
        transition: all 0.2s ease;
        transition-property: left, top;
    }

    .react-grid-item.react-grid-placeholder {
        background: #fee2e2;
        border-radius: 8px;
        border: 2px dashed #b40000;
        opacity: 0.3;
    }

    ${({ isEditable, isMobile }) =>
        isEditable &&
        !isMobile &&
        `
        .react-grid-item {
            cursor: move;
        }

        .react-grid-item:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .react-resizable-handle {
            position: absolute;
            width: 20px;
            height: 20px;
            bottom: 0;
            right: 0;
            background: linear-gradient(135deg, transparent 50%, #b40000 50%);
            cursor: se-resize;
            border-radius: 0 0 8px 0;
            opacity: 0;
            transition: opacity 0.2s ease;
        }

        .react-grid-item:hover .react-resizable-handle {
            opacity: 1;
        }
    `}
`

const DashboardGrid = ({ enabledWidgets, layouts = {}, isEditable = true, onLayoutChange }: DashboardGridProps) => {
    const [width, setWidth] = React.useState(0)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    React.useEffect(() => {
        const container = containerRef.current
        if (!container) return

        // Set initial width
        setWidth(container.offsetWidth)

        // Set up ResizeObserver to track container size changes
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setWidth(entry.contentRect.width)
            }
        })

        resizeObserver.observe(container)

        return () => {
            resizeObserver.unobserve(container)
        }
    }, [])

    const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }
    const cols = { lg: 12, md: 2, sm: 1, xs: 1, xxs: 1 }

    // Add static property to existing layouts based on mobile/editable state
    const layoutsWithStatic = React.useMemo(() => {
        if (Object.keys(layouts).length === 0) return {}
        return Object.keys(layouts).reduce(
            (acc, breakpoint) => {
                acc[breakpoint] = layouts[breakpoint].map((item: WidgetLayout) => ({
                    ...item,
                    static: !isEditable || isMobile,
                }))
                return acc
            },
            {} as Record<string, WidgetLayout[]>,
        )
    }, [layouts, isEditable, isMobile])

    const defaultLayouts: Record<string, WidgetLayout[]> = {
        lg: enabledWidgets.map((id, index) => ({
            i: id,
            x: (index % 3) * 4,
            y: Math.floor(index / 3) * 2,
            w: 4,
            h: 2,
            minW: 1,
            minH: 2,
            maxW: 12,
            maxH: 4,
            static: !isEditable || isMobile,
        })),
        md: enabledWidgets.map((id, index) => ({
            i: id,
            x: (index % 2) * 1,
            y: Math.floor(index / 2) * 2,
            w: 1,
            h: 2,
            minW: 1,
            minH: 2,
            maxW: 2,
            maxH: 4,
            static: !isEditable || isMobile,
        })),
        sm: enabledWidgets.map((id, index) => ({
            i: id,
            x: 0,
            y: index * 2,
            w: 1,
            h: 2,
            minW: 1,
            minH: 2,
            maxW: 1,
            maxH: 4,
            static: !isEditable || isMobile,
        })),
        xs: enabledWidgets.map((id, index) => ({
            i: id,
            x: 0,
            y: index * 2,
            w: 1,
            h: 2,
            minW: 1,
            minH: 2,
            maxW: 1,
            maxH: 4,
            static: !isEditable || isMobile,
        })),
        xxs: enabledWidgets.map((id, index) => ({
            i: id,
            x: 0,
            y: index * 2,
            w: 1,
            h: 2,
            minW: 1,
            minH: 2,
            maxW: 1,
            maxH: 4,
            static: !isEditable || isMobile,
        })),
    }

    const handleLayoutChange = (currentLayout: any, allLayouts: any) => {
        if (onLayoutChange) {
            // Add static property to all layouts based on mobile/editable state
            const layoutsWithStatic = Object.keys(allLayouts).reduce(
                (acc, breakpoint) => {
                    acc[breakpoint] = allLayouts[breakpoint].map((item: WidgetLayout) => ({
                        ...item,
                        static: !isEditable || isMobile,
                    }))
                    return acc
                },
                {} as Record<string, WidgetLayout[]>,
            )
            onLayoutChange(layoutsWithStatic)
        }
    }

    return (
        <GridContainer ref={containerRef}>
            {(Responsive as any)({
                width,
                breakpoints,
                cols,
                layouts: Object.keys(layouts).length > 0 ? layoutsWithStatic : defaultLayouts,
                onLayoutChange: handleLayoutChange,
                isDraggable: isEditable && !isMobile,
                isResizable: isEditable && !isMobile,
                preventCollision: false,
                useCSSTransforms: true,
                compactType: 'vertical',
                margin: [24, 24],
                containerPadding: [0, 0],
                children: enabledWidgets.map((widgetId) => {
                    const WidgetComponent = widgetComponents[widgetId]
                    if (!WidgetComponent) return null
                    return (
                        <div key={widgetId}>
                            <WidgetWrapper isEditable={isEditable} isMobile={isMobile}>
                                <WidgetComponent />
                            </WidgetWrapper>
                        </div>
                    )
                }),
            })}
        </GridContainer>
    )
}

export default DashboardGrid
